import Vector from "victor";
import Mousetrap from "mousetrap";

import { Tool } from "@/wbcontroller/types/Tool";
import ToolType from "@/wbcontroller/types/ToolType";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { ShapeDOMRepresentation } from "../types/ShapeDOMRepresentation";
import { Scope } from "../types/Scope";
import { Selection, SelectionDelegate } from "./Selection";
import Rect from "../types/Rect";
import ClickDetector from "../toolHelpers/ClickDetector";
import DragRectController from "../toolHelpers/DragRectController";
import { SimplePointerEventHandler } from "../SimplePointerEventHandler";
import { ShapeTranslatable2 } from "../types/Shape";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import { DragHandle } from "./DragHandle";
import { WBHUDState } from "@/store/WBHUDState";
import {
  getShapeFillColor,
  getShapeStrokeColor,
  getShapeStrokeWidth,
} from "../util";
import { MultiOperation, Operation } from "../operations/Operation";
import {
  CopyShapesOperation,
  SelectToolInterface,
} from "../operations/CopyShapesOperation";
import { MoveOperation } from "../operations/MoveOperation";
import { DeleteOperation } from "../operations/DeleteOperation";
import { NodeDOMRepresentation } from "../NodeDOMRepresentation";
import { WBSelection } from "../types/WBSelection";

enum State {
  Unknown = "Unknown",
  ExtendingSelection = "ExtendingSelection",
  Translating = "Translating",
  UsingDragHandle = "UsingDragHandle",
}

type SelectToolWhiteboardControllerInterface = WhiteboardControllerInterface & {
  selectionsDidChange: (selections: WBSelection[]) => void;
};

export default class SelectTool
  implements Tool, SelectionDelegate, SelectToolInterface
{
  toolType = ToolType.Select;
  wb?: SelectToolWhiteboardControllerInterface;
  eventHandler: SimplePointerEventHandler;
  selections: Selection[] = [];
  selectionsBeforePointerDown: Selection[] = [];
  didSelectOnPointerDown = false;
  originalScreenPoint = new Vector(0, 0);
  originalWorldPoint = new Vector(0, 0);
  lastScreenPoint = new Vector(0, 0);
  lastWorldPoint = new Vector(0, 0);
  isShiftDown = false;
  isPointerDown = false;
  state = State.Unknown;
  activeDragHandle: DragHandle | null = null;

  permanentSelections: Selection[] = [];
  dragRectController = new DragRectController();
  clickDetector = new ClickDetector();

  constructor() {
    this.eventHandler = new SimplePointerEventHandler();
  }

  setIsShiftDown(value: boolean) {
    this.isShiftDown = value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  settingsDidChange(settings: WBHUDState) {
    const operationsNested = new Array<Array<Operation>>();
    for (const selection of this.selections) {
      operationsNested.push(selection.settingsDidChange(settings));
    }
    const operations = operationsNested.flat();
    if (operations.length) {
      this.wb?.applyOperation(new MultiOperation(operations));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nextZIndexDidChange(nextZIndex: number) {
    // noop
  }

  private getIsSelected(shape: ShapeDOMRepresentation) {
    for (const s of this.selections) {
      if (s.shapeDR === shape) return true;
    }
    return false;
  }

  /* Operations */

  notifySelectionsChanged() {
    if (!this.wb) return;
    this.wb.selectionsDidChange(
      this.selections.map((s) => ({
        subjectID: s.shapeDR.id,
        shapeType: s.shapeDR.shape.shapeType,
        fillColor: getShapeFillColor(s.shapeDR.shape),
        strokeColor: getShapeStrokeColor(s.shapeDR.shape),
        strokeWidth: getShapeStrokeWidth(s.shapeDR.shape),
      }))
    );
  }

  private doesAnySelectionOverlap(worldPoint: Vector): boolean {
    return (
      this.selections.filter((s) => s.shapeDR.hitTest(worldPoint, true))
        .length > 0
    );
  }

  private updateSelectionElTransform = () => {
    if (!this.wb) return;

    for (const selection of this.selections) {
      selection.updateTransform(this.wb.camera);
      // Hide the selection rect specifically for Text shapes (not Node)
      if (
        selection.shapeDR.id === this.wb.drawing.focusedTextShapeID &&
        selection.shapeDR.shape.shapeType === ShapeType.Text
      ) {
        selection.el.style.display = "none";
      } else {
        selection.el.style.display = "block";
      }
    }
  };

  duplicateSelections() {
    const wb = this.wb;
    if (!wb) return;
    const newShapes: [string, ShapeTranslatable2][] = this.selections.map(
      (s) => [
        wb.makeID(),
        {
          ...(s.shapeDR.shape as ShapeTranslatable2),
        },
      ]
    );
    if (!newShapes.length) return;
    wb.applyOperation(new CopyShapesOperation(newShapes, this, wb.drawing));
  }

  applyTranslates(worldPoint: Vector, scope: Scope) {
    const delta = worldPoint.clone().subtract(this.originalWorldPoint);

    if (scope === Scope.Persist) {
      // Special case: save as undoable operation
      for (const s of this.selections) {
        s.resetToOriginalTranslate();
      }

      this.wb?.applyOperation(
        new MoveOperation(
          delta,
          this.selections.map((sel) => sel.translatableSubject)
        )
      );
    } else {
      // Normal case: maintain temporary state
      for (const s of this.selections) {
        s.applyTranslate(scope, delta);
      }
    }
  }

  findSelectionForShape(id: string): Selection | undefined {
    return this.selections.filter((sel) => sel.shapeDR.id === id)[0];
  }

  defocusTextIfMultipleSelections() {
    if (this.selections.length <= 1) return;
    this.wb?.drawing.focusTextShape(null, "defocusTextIfMultipleSelections");
  }

  addSelection(s: ShapeDOMRepresentation) {
    this._selectOne(s);
    this.defocusTextIfMultipleSelections();
    this.updateSelectionElTransform();
    this.notifySelectionsChanged();
  }

  removeFromSelections(s: Selection) {
    this._deselectOne(s);
    this.defocusTextIfMultipleSelections();
    this.notifySelectionsChanged();
  }

  replaceSelections(newSelections: ShapeDOMRepresentation[]) {
    const oldIDs = new Set<string>(this.selections.map((s) => s.shapeDR.id));
    const newIDs = new Set<string>(newSelections.map((s) => s.id));

    for (const s of this.selections) {
      if (newIDs.has(s.shapeDR.id)) continue;
      this._deselectOne(s);
    }

    for (const s of newSelections) {
      if (oldIDs.has(s.id)) continue;
      this._selectOne(s);
    }

    this.defocusTextIfMultipleSelections();
    this.updateSelectionElTransform();
    this.notifySelectionsChanged();
  }

  _selectOne(s: ShapeDOMRepresentation) {
    if (!this.wb) return;
    // skip if already selected
    if (this.selections.filter((s2) => s2.shapeDR === s).length > 0) return;

    const selection = new Selection(this, this.wb, s);
    this.selections.push(selection);
    this.wb.el.appendChild(selection.el);
    selection.didMount();
  }

  _deselectOne(s: Selection) {
    if (s.shapeDR.id === this.wb?.drawing.focusedTextShapeID) {
      this.wb?.drawing.focusTextShape(null, "deselected");
    }
    s.el.parentElement?.removeChild(s.el);
    s.willUnmount();
    this.selections = this.selections.filter((s2) => s2 !== s);
  }

  deleteAll() {
    this.wb?.applyOperation(
      new DeleteOperation(
        this.selections.map((sel) => sel.shapeDR.id),
        this.wb.shapeStore
      )
    );
    this.replaceSelections([]);
    return false;
  }

  deleteOne(s: Selection) {
    this.wb?.applyOperation(
      new DeleteOperation([s.shapeDR.id], this.wb.shapeStore)
    );
    this._deselectOne(s);
    this.notifySelectionsChanged();
  }

  extendSelection(wr: Rect) {
    if (!this.wb) return;

    const persistedShapes = this.permanentSelections.map((s) => s.shapeDR);
    const filteredNewShapes = this.findSelectableShapes(wr).filter(
      (s) => persistedShapes.indexOf(s) === -1
    );
    this.replaceSelections(persistedShapes.concat(filteredNewShapes));
  }

  findSelectableShapes(wr: Rect): Array<ShapeDOMRepresentation> {
    if (!this.wb) return [];
    return this.wb.drawing.orderedShapes.filter((shapeDR) => {
      return shapeDR.maySelect && shapeDR.hitTestRect(wr);
    });
  }

  findTopmostShapeExactlyCoveringPoint(
    shapes: ShapeDOMRepresentation[],
    worldPoint: Vector
  ): ShapeDOMRepresentation | null {
    const reversedShapes = new Array<ShapeDOMRepresentation>()
      .concat(shapes)
      .reverse();
    for (const shape of reversedShapes) {
      if (shape.hitTest(worldPoint, false)) {
        return shape;
      }
    }
    for (const shape of reversedShapes) {
      if (shape.hitTest(worldPoint, true)) {
        return shape;
      }
    }
    return null;
  }

  findBestSelectionCandidate(
    shapes: ShapeDOMRepresentation[],
    worldPoint: Vector
  ): ShapeDOMRepresentation {
    return (
      this.findTopmostShapeExactlyCoveringPoint(shapes, worldPoint) ||
      shapes[shapes.length - 1]
    );
  }

  /* Event handlers */

  onPointerDown = (screenPoint: Vector) => {
    this.isPointerDown = true;
    if (!this.wb) return;
    this.originalScreenPoint = screenPoint;
    this.originalWorldPoint = this.wb.camera.convertScreenToWorld(screenPoint);
    this.lastScreenPoint = this.originalScreenPoint;
    this.lastWorldPoint = this.originalWorldPoint;

    this.didSelectOnPointerDown = false;
    this.selectionsBeforePointerDown = new Array<Selection>().concat(
      this.selections
    );

    const wp = this.originalWorldPoint;

    // Short circuit if we're on a drag handle
    for (const s of this.selections) {
      // screenPoint is relative to wb.el, not the actual screen. normally
      // this is desired but in this case it isn't.
      const h = s.getDragHandle(this.eventHandler.clientPoint);
      if (h) {
        this.activeDragHandle = h;
        this.state = State.UsingDragHandle;
        this.activeDragHandle.onPointerDown(this.lastScreenPoint, this.wb);
        return;
      }
    }

    this.clickDetector.onPointerDown(screenPoint);

    const beginDrag = () => {
      this.state = State.Translating;
      for (const s of this.selections) {
        s.setOriginalTranslate();
      }
    };

    const selectableShapesHere = this.findSelectableShapes(new Rect(wp, wp));
    const topShapeUnderCursor = this.findBestSelectionCandidate(
      selectableShapesHere,
      wp
    );
    const isTopShapeUnderCursorInSelection =
      this.selections.filter((sel) => sel.shapeDR === topShapeUnderCursor)
        .length > 0;

    if (this.doesAnySelectionOverlap(wp) && !this.isShiftDown) {
      beginDrag();
    } else if (
      !this.isShiftDown &&
      topShapeUnderCursor != null &&
      !isTopShapeUnderCursorInSelection
    ) {
      this.didSelectOnPointerDown = true;
      this.replaceSelections([topShapeUnderCursor]);
      beginDrag();
    } else {
      // console.log(
      //   this.isShiftDown,
      //   topShapeUnderCursor,
      //   isTopShapeUnderCursorInSelection
      // );
      if (!this.isShiftDown) {
        this.replaceSelections([]);
      }
      this.state = State.ExtendingSelection;
      this.permanentSelections = new Array<Selection>().concat(this.selections);
      this.dragRectController.onPointerDown(this.wb, screenPoint);
    }
  };

  onPointerMove = (screenPoint: Vector) => {
    if (!this.isPointerDown) return;
    this.clickDetector.onPointerMove(screenPoint);
    if (!this.wb) return;
    this.lastScreenPoint = screenPoint;
    this.lastWorldPoint = this.wb.camera.convertScreenToWorld(screenPoint);

    switch (this.state) {
      case State.UsingDragHandle:
        this.activeDragHandle?.onPointerDrag(this.lastScreenPoint, this.wb);
        this.updateSelectionElTransform();
        break;
      case State.Translating:
        this.applyTranslates(this.lastWorldPoint, Scope.Live);
        this.updateSelectionElTransform();
        break;
      case State.ExtendingSelection:
        this.dragRectController.onPointerMove(this.wb, screenPoint);

        this.extendSelection(
          new Rect(this.originalWorldPoint, this.lastWorldPoint)
        );
        break;
    }
  };

  onPointerUp = () => {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.dragRectController.abort();
    if (this.state === State.UsingDragHandle && this.wb) {
      this.activeDragHandle?.onPointerUp(this.lastScreenPoint, this.wb);
    } else if (!this.clickDetector.isClickAborted) {
      this.onClick(this.clickDetector.lastScreenPoint);
    } else if (this.state === State.Translating) {
      this.applyTranslates(this.lastWorldPoint, Scope.Persist);
    } else if (this.state === State.ExtendingSelection) {
      /* lock in selection */
    }
    this.abort();
  };

  onClick = (screenPoint: Vector) => {
    if (!this.wb) return;

    // console.group("Click");

    const drawing = this.wb.drawing;
    const worldPoint = this.wb.camera.convertScreenToWorld(screenPoint);

    const shapesWithOverlappingRects = drawing.orderedShapes.filter((s) => {
      return s.maySelect && s.hitTest(worldPoint, true);
    });

    // If clicked on nothing, deselect everything
    if (shapesWithOverlappingRects.length < 1) {
      // console.log("Clicked on nothing");
      if (!this.isShiftDown) {
        // console.log("Shift not held; deselecting everything");
        this.replaceSelections([]);
      }
      // console.groupEnd();
      return;
    }

    // If shift key down and something selected, deselect that topmost item
    if (this.isShiftDown) {
      const shapeToDeselect = this.findTopmostShapeExactlyCoveringPoint(
        this.selections.map((sel) => sel.shapeDR),
        worldPoint
      );
      if (shapeToDeselect) {
        // console.log("Deselecting a shape you clicked");
        const selectionForDeselectedShape = this.findSelectionForShape(
          shapeToDeselect.id
        );
        if (selectionForDeselectedShape)
          this.removeFromSelections(selectionForDeselectedShape);
        // console.groupEnd();
        return;
      }
    }

    const selectionCandidate = this.findBestSelectionCandidate(
      shapesWithOverlappingRects,
      worldPoint
    );

    // If nothing selected, select "best" candidate
    if (this.selections.length !== 1 && !this.isShiftDown) {
      // console.log(
      //   "Clicked on something while nothing was selected; picking best candidate"
      // );
      this.addSelection(selectionCandidate);
      // console.groupEnd();
      return;
    }
    // console.log(this.selections);

    let soleShapeType: ShapeType | undefined = undefined;
    let soleShapeID: string | undefined;
    if (this.selections.length === 1) {
      soleShapeType = this.selections[0].shapeDR.shape.shapeType;
      soleShapeID = this.selections[0].shapeDR.id;
    }

    // If a texty object is already selected, and the click is in that texty object,
    // focus the texty object
    if (
      this.selectionsBeforePointerDown.length === 1 &&
      soleShapeID &&
      this.selectionsBeforePointerDown[0].shapeDR.id === soleShapeID &&
      (soleShapeType === ShapeType.Text || soleShapeType === ShapeType.Node) &&
      this.selectionsBeforePointerDown[0].shapeDR.hitTest(worldPoint, true)
    ) {
      this.wb.drawing.focusTextShape(soleShapeID, "select tool click");
      return;
    }

    // If clicking on one thing, select that thing
    if (shapesWithOverlappingRects.length == 1) {
      // console.log("Clicked on exactly one thing. Selecting it.");
      this.addSelection(selectionCandidate);
      // console.groupEnd();
      return;
    }

    // If we didn't already select something, cycle through shapes
    // under cursor on each click
    if (!this.didSelectOnPointerDown && this.selections.length > 0) {
      // console.log("Multiple candidates available. Doing magic.");
      const selectedShapeZIndex = this.selections[0].shapeDR.shape.zIndex;

      // If something selected, select next lowest item if possible, otherwise select topmost,
      // so that repeated clicking will cycle top to bottom and back to the top again
      const shapesWithLowerZIndices = shapesWithOverlappingRects.filter(
        (s) => s.shape.zIndex < selectedShapeZIndex
      );
      // console.log(
      //   selectedShapeZIndex,
      //   shapesWithOverlappingRects.map((s) => s.shape)
      // );
      let shapeToSelect: ShapeDOMRepresentation | undefined;
      if (shapesWithLowerZIndices.length < 1) {
        // console.log("Trying to select topmost overlapping shape");
        shapeToSelect =
          shapesWithOverlappingRects[shapesWithOverlappingRects.length - 1];
      } else {
        // console.log("Trying to select a shape behind");
        shapeToSelect =
          shapesWithLowerZIndices[shapesWithLowerZIndices.length - 1];
      }
      if (shapeToSelect) {
        if (this.isShiftDown) {
          this.addSelection(shapeToSelect);
        } else {
          this.replaceSelections([shapeToSelect]);
        }
      }
    }
    // console.groupEnd();
  };

  abort = () => {
    this.isPointerDown = false;
    this.state = State.Unknown;
    this.activeDragHandle = null;
    this.clickDetector.abort();
    if (!this.wb) return;
    this.updateSelectionElTransform();
  };

  /* Lifecycle */

  // When user selects a sticky via Quill's element, also add it to the selection
  onFocusChanged = (id: string | null) => {
    if (id === null) return;
    const stickyDR = this.wb?.drawing.indexedShapes[
      id
    ] as NodeDOMRepresentation;
    if (!stickyDR) return;
    this.replaceSelections([stickyDR]);
  };

  bind(wb: WhiteboardControllerInterface) {
    this.wb = wb as SelectToolWhiteboardControllerInterface;
    this.eventHandler.bind(wb, this);
    this.isShiftDown = false;
    this.isPointerDown = false;
    this.state = State.Unknown;
    for (const s of this.selections) {
      this.wb.el.appendChild(s.el);
      s.updateTransform(wb.camera);
    }
    Mousetrap.bind("backspace", () => this.deleteAll());

    wb.camera.allChanges.onValue(this.updateSelectionElTransform);

    this.wb.drawing.focusedTextShapeIDBus.stream.onValue(this.onFocusChanged);
  }

  // eslint-disable-next-line
  unbind(wb: WhiteboardControllerInterface) {
    this.wb?.drawing.focusedTextShapeIDBus.stream.offValue(this.onFocusChanged);

    for (const s of this.selections) {
      s.el.parentElement?.removeChild(s.el);
    }
    // make extra sure this gets cleaned up
    this.dragRectController.abort();
    this.eventHandler.unbind();
    this.wb = undefined;
    Mousetrap.unbind("backspace");

    wb.camera.allChanges.offValue(this.updateSelectionElTransform);
  }

  /* SelectionDelegate */

  selectionDidRequestDelete(selection: Selection) {
    this.deleteOne(selection);
  }
}
